import { getTranslation } from '@nexelem/utils';
import { FORMAT_ELEMENT_COMMAND, INDENT_CONTENT_COMMAND, LexicalEditor, OUTDENT_CONTENT_COMMAND } from 'lexical';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '../ActionButton';
import { ButtonWithMenu } from '../ButtonWithMenu';
import { IconButton } from '../IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
  faIndent,
  faOutdent,
} from '@fortawesome/pro-light-svg-icons';

type AlignDropDownProps = {
  readonly disabled?: boolean;
  readonly editor: LexicalEditor;
  readonly isRTL: boolean;
};

export const AlignDropDown = ({ disabled, editor, isRTL }: AlignDropDownProps) => {
  const { t: translate } = useTranslation();
  const t = useMemo(() => getTranslation(translate, 'components.richTextEditor.aligners'), [translate]);

  const onClickLeftAlign = useCallback(() => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
  }, [editor]);

  const onClickRightAlign = useCallback(() => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
  }, [editor]);

  const onClickCenterAlign = useCallback(() => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
  }, [editor]);

  const onClickJustifyAlign = useCallback(() => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
  }, [editor]);

  const onClickIndent = useCallback(() => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const onClickOutdent = useCallback(() => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  return (
    <ButtonWithMenu
      disabled={disabled}
      openingElement={<IconButton StartIcon={<FontAwesomeIcon icon={faAlignLeft} />} variant="text" />}
      content={() => (
        <>
          <ActionButton Icon={<FontAwesomeIcon icon={faAlignLeft} />} label={t('left')} onClick={onClickLeftAlign} />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faAlignCenter} />}
            label={t('center')}
            onClick={onClickCenterAlign}
          />
          <ActionButton Icon={<FontAwesomeIcon icon={faAlignRight} />} label={t('right')} onClick={onClickRightAlign} />
          <ActionButton
            Icon={<FontAwesomeIcon icon={faAlignJustify} />}
            label={t('justify')}
            onClick={onClickJustifyAlign}
          />
          <ActionButton
            Icon={isRTL ? <FontAwesomeIcon icon={faIndent} /> : <FontAwesomeIcon icon={faOutdent} />}
            label={t('outdent')}
            onClick={onClickOutdent}
          />
          <ActionButton
            Icon={isRTL ? <FontAwesomeIcon icon={faOutdent} /> : <FontAwesomeIcon icon={faIndent} />}
            label={t('indent')}
            onClick={onClickIndent}
          />
        </>
      )}
    />
  );
};

AlignDropDown.displayName = 'AlignDropDown';
